import { useCallback, useEffect, useState } from 'react';
import {
  deletePackageLoanTape,
  getPackageLoanTape,
  LOAN_TAPE_ACCEPT,
  uploadPackageLoanTape,
} from '../../api/originatorApi';
import { Button } from '../ui/Button';
import { formatCustodyFailure } from '../../api/apiError';

function formatBytes(value) {
  const bytes = Number(value || 0);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Loan tape attached to a package: name, rows, SHA-256, matched row, and
 * Replace / Remove while the backend still allows it (not CERTIFIED).
 * Renders only backend values; "No loan tape attached" when the GET is 404.
 */
export function LoanTapeCard({ packageId, canManage = true, inCustody = false, onChanged }) {
  const [tape, setTape] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showRow, setShowRow] = useState(false);

  const load = useCallback(async () => {
    if (!packageId) return;
    setLoading(true);
    setError('');
    try {
      const response = await getPackageLoanTape(packageId);
      setTape(response);
    } catch (requestError) {
      setTape(null);
      if (requestError?.status !== 404) {
        setError(requestError.message || 'Unable to load the loan tape.');
      }
    } finally {
      setLoading(false);
    }
  }, [packageId]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleChoose(event) {
    const [file] = Array.from(event.target.files || []);
    event.target.value = '';
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const response = await uploadPackageLoanTape(packageId, file);
      setTape(response);
      onChanged?.(response);
    } catch (requestError) {
      setError(formatCustodyFailure(requestError, 'Loan tape could not be attached.'));
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove() {
    setBusy(true);
    setError('');
    try {
      await deletePackageLoanTape(packageId);
      setTape(null);
      onChanged?.(null);
    } catch (requestError) {
      setError(formatCustodyFailure(requestError, 'Loan tape could not be removed.'));
    } finally {
      setBusy(false);
    }
  }

  const manageable = !inCustody && canManage && (tape ? tape.can_replace : true);
  const matched = tape?.matched_row || null;

  return (
    <section
      aria-label="Loan tape"
      className="rounded-2xl border border-eorig-border bg-white p-5 shadow-eorig-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-eorig-ink">Loan tape</h2>
          <p className="mt-1 text-[12px] text-eorig-muted">
            Compared with extracted fields by column name under Digitize → Validation.
          </p>
          {inCustody ? (
            <p className="mt-1 text-[12px] text-sky-800" data-testid="loan-tape-in-custody">
              In bank custody — the loan tape is frozen.
            </p>
          ) : null}
        </div>
        {manageable ? (
          <label
            className={`inline-flex cursor-pointer items-center justify-center rounded-full border border-eorig-border bg-white px-4 py-2 text-sm font-semibold text-eorig-ink transition-colors hover:bg-eorig-surface ${
              busy ? 'pointer-events-none opacity-50' : ''
            }`}
          >
            {tape ? 'Replace' : 'Attach tape'}
            <input
              type="file"
              accept={LOAN_TAPE_ACCEPT}
              className="sr-only"
              onChange={handleChoose}
              disabled={busy}
              data-testid="loan-tape-card-input"
            />
          </label>
        ) : null}
      </div>

      {loading ? (
        <p className="mt-3 text-sm text-eorig-muted">Loading loan tape…</p>
      ) : tape ? (
        <dl className="mt-3 grid gap-x-4 gap-y-2 text-sm sm:grid-cols-[auto_1fr]">
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">File</dt>
          <dd className="min-w-0 break-all text-eorig-ink">
            {tape.file_name}{' '}
            <span className="text-eorig-muted">({formatBytes(tape.size_bytes)})</span>
          </dd>
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">Rows</dt>
          <dd className="text-eorig-ink">
            {tape.row_count} row(s) · {tape.headers.length} column(s)
          </dd>
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">
            SHA-256
          </dt>
          <dd className="min-w-0 break-all font-mono text-[12px] text-eorig-ink">
            {tape.content_sha256}
          </dd>
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">
            Matched row
          </dt>
          <dd className="text-eorig-ink">
            {matched ? (
              <>
                {matched.matched_on ? `Matched on ${matched.matched_on}` : 'Single-row tape'}
                {' · '}
                <button
                  type="button"
                  className="font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
                  onClick={() => setShowRow((value) => !value)}
                >
                  {showRow ? 'Hide values' : 'Show values'}
                </button>
              </>
            ) : (
              'No row matches this package yet (loan number is set at certification).'
            )}
          </dd>
          {!tape.can_replace ? (
            <>
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">
                Locked
              </dt>
              <dd className="text-eorig-ink">Package is certified; the tape is part of the attestation.</dd>
            </>
          ) : null}
        </dl>
      ) : (
        <p className="mt-3 text-sm text-eorig-muted">No loan tape attached.</p>
      )}

      {showRow && matched ? (
        <table className="mt-3 w-full text-left text-[12px] text-eorig-ink">
          <caption className="sr-only">Matched loan tape row</caption>
          <tbody>
            {Object.entries(matched.values).map(([label, value]) => (
              <tr key={label} className="border-t border-eorig-border">
                <th scope="row" className="py-1 pr-3 font-medium text-eorig-muted">
                  {label}
                </th>
                <td className="break-all py-1">{value || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {tape && manageable ? (
        <div className="mt-3">
          <Button variant="ghost" onClick={handleRemove} disabled={busy}>
            Remove tape
          </Button>
        </div>
      ) : null}

      {error ? (
        <p className="mt-3 text-sm text-rose-600" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}
